import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { AssignmentEditor, RULE_FIELDS, ruleIncomplete } from "@/components/RuleReassign";

const BULK_ROLES = ["super_admin", "admin", "admission_manager", "senior_counselor", "team_lead"];
export const useIsAdmin = () => ["super_admin", "admin"].includes(useAuth().user?.role);

// Admin-only single-record delete column for Leads / Prospects tables
export function rowDeleteColumn(entity, enabled, onDone) {
  if (!enabled) return { key: "_del", label: "", hidden: true, render: () => null };
  return { key: "_del", label: "", fixed: true, render: (r) => <RowDelete entity={entity} row={r} onDone={onDone} /> };
}

function RowDelete({ entity, row, onDone }) {
  const [open, setOpen] = useState(false);
  const name = row.student_name || row.name;
  const run = async () => {
    try { await api.post(`/${entity}/bulk-delete`, { ids: [row.id] }); toast.success(`${name} deleted`); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen(true)} className="w-7 h-7 rounded-md hover:bg-rose-50 text-rose-500 flex items-center justify-center" title="Delete" data-testid={`row-delete-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Delete {name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{entity === "leads" ? "The lead and its open tasks/follow-ups are removed." : "The prospect is permanently removed."} This cannot be undone.</p>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button variant="destructive" onClick={run} data-testid={`row-delete-confirm-${row.id}`}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}

// Admin-only delete bar for pages without Bulk Edit (Students, Counselling)
export function AdminDeleteBar({ path, label, sel, setSel, onDone, note }) {
  const [open, setOpen] = useState(false);
  if (!sel.length) return null;
  const run = async () => {
    try { const { data } = await api.post(path, { ids: sel }); toast.success(`${data.deleted} ${label} deleted${data.skipped_with_admission ? ` · ${data.skipped_with_admission} skipped (have admissions)` : ""}`); setOpen(false); setSel([]); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 mb-3 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 text-sm" data-testid={`${label}-delete-bar`}>
      <span className="font-semibold text-rose-700">{sel.length} selected</span>
      <Button size="sm" variant="destructive" className="h-8" onClick={() => setOpen(true)} data-testid={`${label}-bulk-delete-btn`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
      <button onClick={() => setSel([])} className="ml-auto text-slate-500"><X className="w-4 h-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Delete {sel.length} {label}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{note} This cannot be undone.</p>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button variant="destructive" onClick={run} data-testid={`${label}-bulk-delete-confirm`}>Delete {sel.length}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
export const useCanBulk = () => BULK_ROLES.includes(useAuth().user?.role);

export const selectColumn = (rows, sel, setSel, enabled = true) => (!enabled ? { key: "_sel", label: "", hidden: true, render: () => null } : {
  key: "_sel", fixed: true,
  label: <input type="checkbox" className="accent-[#4F46E5]" checked={!!rows?.length && rows.every((r) => sel.includes(r.id))} onChange={(e) => setSel(e.target.checked ? (rows || []).map((r) => r.id) : [])} data-testid="bulk-select-all" />,
  render: (r) => <input type="checkbox" className="accent-[#4F46E5]" checked={sel.includes(r.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => setSel(e.target.checked ? [...sel, r.id] : sel.filter((x) => x !== r.id))} data-testid={`bulk-select-${r.id}`} />,
});

// fields: [{ key, label, options? (array) , type? }]
export default function BulkEdit({ entity, sel, setSel, fields, users: given, onDone, tags }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [loaded, setLoaded] = useState([]);
  const [del, setDel] = useState(false);
  useEffect(() => { if (open && !given) api.get("/users").then(({ data }) => setLoaded(data.filter((u) => u.active !== false && ["counselor", "senior_counselor", "telecaller", "team_lead", "admission_manager"].includes(u.role)))).catch(() => {}); }, [open, given]);
  const users = given || loaded;
  const role = useAuth().user?.role;
  const clearBtn = (k) => (f[k] ? <button onClick={() => { const n = { ...f }; delete n[k]; setF(n); }} className="text-[10px] text-slate-400 ml-1 underline">no change</button> : null);
  if (!sel.length || !BULK_ROLES.includes(role)) return null;
  const save = async () => {
    const { assigned_to, add_tags, assign, ...updates } = f;
    const a = assign || {};
    if (a.mode === "advanced" && (a.rules || []).some(ruleIncomplete)) { toast.error("Complete every condition (field, value and counselors)"); return; }
    const doAssign = (a.to || []).length || (a.mode === "advanced" && (a.rules || []).length);
    try {
      let msg = "";
      if (Object.keys(updates).length || add_tags) {
        const { data } = await api.post(`/${entity}/bulk-update`, { ids: sel, updates, add_tags: add_tags ? add_tags.split(",").map((t) => t.trim()).filter(Boolean) : undefined });
        msg = `${data.updated} record(s) updated`;
      }
      if (doAssign) {
        const { data } = await api.post(`/${entity}/rule-reassign`, { ids: sel, rules: a.mode === "advanced" ? a.rules : [], fallback: a.to || [] });
        msg = `${msg ? msg + " · " : ""}${data.reassigned} assigned (${Object.entries(data.by_user).map(([n, c]) => `${n}: ${c}`).join(", ") || "none matched"})`;
      }
      if (!msg) { toast.error("Nothing to change — fill a field or pick counselors"); return; }
      toast.success(msg); setOpen(false); setF({}); setSel([]); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { const { data } = await api.post(`/${entity}/bulk-delete`, { ids: sel }); toast.success(`${data.deleted} record(s) deleted`); setDel(false); setSel([]); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canDelete = ["super_admin", "admin"].includes(role);
  return (
    <div className="flex items-center gap-2 mb-3 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-sm" data-testid={`${entity}-bulk-bar`}>
      <span className="font-semibold text-[#312E81]">{sel.length} selected</span>
      <Button size="sm" className="h-8 bg-[#4F46E5] hover:bg-[#4338CA]" onClick={() => setOpen(true)} data-testid={`${entity}-bulk-edit-btn`}><Pencil className="w-3.5 h-3.5 mr-1" /> Bulk Edit</Button>
      {canDelete && <Button size="sm" variant="outline" className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => setDel(true)} data-testid={`${entity}-bulk-delete-btn`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
      <Dialog open={del} onOpenChange={setDel}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Delete {sel.length} {sel.length === 1 ? entity.slice(0, -1) : entity}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{entity === "leads" ? "The selected leads and their open tasks/follow-ups are removed. Student profiles, applications and admissions are kept." : "The selected prospects are permanently removed. Leads already created from them are kept."} This cannot be undone.</p>
          <DialogFooter><Button variant="outline" onClick={() => setDel(false)}>Cancel</Button><Button variant="destructive" onClick={remove} data-testid={`${entity}-bulk-delete-confirm`}>Delete {sel.length}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <button onClick={() => setSel([])} className="ml-auto text-xs text-slate-500 flex items-center gap-1" data-testid={`${entity}-bulk-clear`}><X className="w-3.5 h-3.5" /> Clear selection</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Bulk Edit {sel.length} {entity}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Only the fields you fill in are changed. Leave the rest blank.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3"><Label>Assign / reassign {f.assign && <button onClick={() => { const n = { ...f }; delete n.assign; setF(n); }} className="text-[10px] text-slate-400 ml-1 underline">no change</button>}</Label>
              <div className="mt-1"><AssignmentEditor users={users} fields={RULE_FIELDS[entity] || RULE_FIELDS.prospects} value={f.assign || {}} onChange={(v) => setF({ ...f, assign: v })} testid="bulk-assign" /></div>
              <p className="text-[11px] text-slate-400 mt-1">Applies only to the {sel.length} selected {entity}. Converted / closed records are skipped.</p></div>
            {fields.map((fd) => (
              <div key={fd.key}><Label>{fd.label} {clearBtn(fd.key)}</Label>
                {fd.options ? (
                  <Select value={f[fd.key] || ""} onValueChange={(v) => setF({ ...f, [fd.key]: v })}><SelectTrigger data-testid={`bulk-${fd.key}`}><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">{fd.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                ) : <Input type={fd.type || "text"} value={f[fd.key] || ""} onChange={(e) => setF({ ...f, [fd.key]: e.target.value })} placeholder="No change" data-testid={`bulk-${fd.key}`} />}
              </div>
            ))}
            {tags && <div className="col-span-2"><Label>Add tags (comma separated)</Label><Input value={f.add_tags || ""} onChange={(e) => setF({ ...f, add_tags: e.target.value })} data-testid="bulk-add_tags" /></div>}
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="bulk-save">Apply to {sel.length}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
